import { ThemeEvents } from "@theme/events";

export class HeaderMenuPopupComponent extends HTMLElement {
    toggler = '[toggle-active-state]';

    #state = {
        /**@type {HTMLElement | null} */
        activeItem: null
    }
    
    constructor() {
        super();
        if (typeof $ !== 'function') {
            throw new Error('jQuery is not detected at HeaderMenuPopupComponent');
        }
    }

    connectedCallback() {
        this.#initListeners();
    }

    disconnectedCallback() {
        this.#destroyListeners();
    }

    /**
     * @param {PointerEvent} event 
     */
    toggleActiveState(event) {
        const target = event.target instanceof HTMLElement ? event.target : null;
        const li = target?.closest('li');
        const anchor = target?.closest('a.menu-list__link');

        if (!anchor || !li) return;

        if (this.#state.activeItem?.id !== li?.id) {
            this.#deactivate();
            setTimeout(() => this.#activate(li), 100);
        } else {
            this.#deactivate();
        }
    }

    /**
     * @param {HTMLElement} item 
     */
    #activate(item) {
        const subMenu = $(item).find('[ref="submenu[]"]').first();
        const anchor = $(item).find('a.menu-list__link').first();
        $(anchor).attr('aria-expanded', 'true');
        this.#positionDropdown(subMenu.get(0), anchor.get(0));
        $(subMenu).fadeIn(150);
        this.#state.activeItem = item;
    }

    /**
     * Simple dropdowns (menu items without nested links) sit under their own
     * menu link instead of spanning the header like the mega menu.
     * @param {HTMLElement | undefined} subMenu
     * @param {HTMLElement | undefined} anchor
     */
    #positionDropdown(subMenu, anchor) {
        if (!subMenu || !anchor || !subMenu.classList.contains('menu-list__submenu--dropdown')) return;
        // Measure while hidden: show it invisibly for a moment
        const prevDisplay = subMenu.style.display;
        const prevVisibility = subMenu.style.visibility;
        subMenu.style.visibility = 'hidden';
        subMenu.style.display = 'block';
        subMenu.style.left = '0px';
        const parent = subMenu.offsetParent || document.body;
        const parentRect = parent.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();
        // Line the list's text up with the menu link (inner padding is 28px)
        let left = anchorRect.left - parentRect.left - 28;
        const maxLeft = parentRect.width - subMenu.offsetWidth - 16;
        subMenu.style.left = `${Math.max(0, Math.min(left, maxLeft))}px`;
        subMenu.style.display = prevDisplay;
        subMenu.style.visibility = prevVisibility;
    }

    /**
     * @returns {void}
     */
    #deactivate() {
        const subMenu = $(this.#state.activeItem).find('[ref="submenu[]"]').first();
        const anchor = $(this.#state.activeItem).find('a.menu-list__link').first();
        $(subMenu).fadeOut(150);
        $(anchor).removeAttr('aria-expanded');
        this.#state.activeItem = null;
    }

    /**
     * @param {PointerEvent} event
     */
    #handleDocumentClick(event) {
        const target = event.target instanceof HTMLElement ? event.target : null;
        if (this.#state.activeItem && !this.contains(target)) {
            this.#deactivate();
        }
    }

    /**
     * @returns {void}
     */
    #initListeners() {
        const togglers = this.querySelectorAll(this.toggler);
        for (const toggler of togglers) {
            toggler.addEventListener('click', this.toggleActiveState.bind(this));
        }
        document.addEventListener('click', this.#handleDocumentClick.bind(this));
        document.addEventListener(ThemeEvents.cartUpdate, this.#deactivate.bind(this));
    }

    /**
     * @returns {void}
     */
    #destroyListeners() {
        const togglers = this.querySelectorAll(this.toggler);
        for (const toggler of togglers) {
            toggler.removeEventListener('click', this.toggleActiveState.bind(this));
        }
    }
}

if (!customElements.get('header-menu-popup')) {
    customElements.define('header-menu-popup', HeaderMenuPopupComponent);
}